import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { SESSION_EXPIRED_EVENT, keepPageOpen, renewSession, sessionExpired } from '../../services/session';
import { MeetingCapture } from './recorderEngine';
import { UploadQueue } from './uploadQueue';

// Enregistrement en cours, porté au niveau de l'application : il continue quand on change de page.
// phase : idle | starting | recording | sending | done | error
//         | conflict (un collègue enregistre déjà la réunion) | joined (suivie sans enregistrer)

const MeetingRecorderContext = createContext(null);

const httpError = async (response) => {
  const body = await response.json().catch(() => ({}));
  const detail = typeof body.detail === 'string' ? body.detail : body.detail?.message;
  const error = new Error(detail || `Erreur ${response.status}`);
  error.status = response.status;
  error.detail = body.detail;
  // Session expirée : l'audio reste gardé et repart une fois reconnecté (dans un autre onglet).
  if (response.status === 401) sessionExpired();
  // 4xx : inutile d'insister (réunion close, droits, taille) ; 5xx, réseau et session expirée : on réessaie.
  error.fatal = response.status >= 400 && response.status < 500
    && ![401, 408, 429].includes(response.status);
  return error;
};

export const MeetingRecorderProvider = ({ children }) => {
  const [state, setState] = useState({ phase: 'idle' });
  const [level, setLevel] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [upload, setUpload] = useState({ pending: 0, retrying: false });
  const captureRef = useRef(null);
  const queueRef = useRef(null);
  const wakeLockRef = useRef(null);
  const meetingRef = useRef(null);
  const stoppedRef = useRef(null); // { chunkCount, duration } une fois la captation terminée

  const patch = useCallback((values) => setState((prev) => ({ ...prev, ...values })), []);

  const keepScreenOn = useCallback(async () => {
    try {
      wakeLockRef.current = await navigator.wakeLock?.request('screen');
    } catch {
      // Pas de verrou d'écran (navigateur, économie d'énergie) : l'enregistrement continue.
    }
  }, []);

  const releaseScreen = useCallback(() => {
    wakeLockRef.current?.release().catch(() => {});
    wakeLockRef.current = null;
  }, []);

  useEffect(() => {
    if (state.phase !== 'recording') return undefined;
    const timer = setInterval(() => setElapsed(captureRef.current?.elapsedSeconds || 0), 500);
    // Un téléphone qui repasse au premier plan perd son verrou d'écran : on le reprend.
    const onVisible = () => { if (document.visibilityState === 'visible') keepScreenOn(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [state.phase, keepScreenOn]);

  // Tant que de l'audio n'est pas parti, la page reste ouverte : avertissement à la fermeture, et une session
  // expirée ne renvoie pas vers /login (voir services/session.js).
  const holding = state.phase === 'recording' || state.phase === 'sending' || upload.pending > 0
    || (state.phase === 'error' && state.canRetry);
  useEffect(() => {
    if (!holding) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    const release = keepPageOpen();
    const expired = () => setState((prev) => (prev.sessionExpired ? prev : { ...prev, sessionExpired: true }));
    // Reconnexion dans un autre onglet : le nouveau jeton sert aussitôt aux envois en attente.
    const signedIn = (event) => {
      if (event.key !== 'auth_token' || !event.newValue) return;
      setState((prev) => (prev.sessionExpired ? { ...prev, sessionExpired: false } : prev));
      queueRef.current?.enqueue([]);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, expired);
    window.addEventListener('storage', signedIn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener(SESSION_EXPIRED_EVENT, expired);
      window.removeEventListener('storage', signedIn);
      release();
    };
  }, [holding]);

  const sendChunk = useCallback(async (index, blob) => {
    const meeting = meetingRef.current;
    const response = await fetch(`${apiService.getBaseUrl()}/meetings/${meeting.id}/chunks/${index}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream', ...apiService.getHeaders() },
      body: blob,
    });
    if (!response.ok) throw await httpError(response);
  }, []);

  const start = useCallback(async (request) => {
    // Portée : un projet, ou un client ou un programme (Nexus dira quels projets la réunion concerne), ou aucune.
    const { projectId, clientId, programmeId, projectName, title, source, participantIds, calendar, hideTranscript,
      allowDuplicate } = request;
    if (captureRef.current) return;
    patch({ phase: 'starting', error: null, warning: null, canRetry: false, meeting: null, projectName,
      sessionExpired: false });
    setElapsed(0);
    renewSession(); // jeton de plus d'un jour : prolongé avant une longue réunion
    const capture = new MeetingCapture({
      source,
      onChunk: (index, blob) => queueRef.current?.push(index, blob),
      onLevel: setLevel,
      onTabAudioEnded: () => patch({ warning: "Le partage de l'onglet s'est arrêté : seul ton micro est encore enregistré." }),
    });
    try {
      await capture.open();
      const meeting = await apiService.post('/meetings', {
        project_id: projectId || null, client_id: clientId || null, programme_id: programmeId || null,
        title, source, mime_type: capture.mimeType, participant_ids: participantIds,
        hide_transcript: Boolean(hideTranscript), allow_duplicate: Boolean(allowDuplicate),
        external_attendees: calendar?.external_attendees || [],
        calendar_event_id: calendar?.event?.id || null, calendar_series_id: calendar?.event?.series_id || null,
      });
      meetingRef.current = meeting;
      queueRef.current = new UploadQueue({
        send: sendChunk,
        onChange: setUpload,
        onError: (error) => patch({ warning: `Envoi interrompu : ${error.message}` }),
      });
      captureRef.current = capture;
      capture.record();
      keepScreenOn();
      patch({ phase: 'recording', meeting });
    } catch (error) {
      capture.release();
      if (error?.detail?.code === 'already_recording') {
        patch({ phase: 'conflict', conflict: { ...error.detail, request } });
        return;
      }
      patch({ phase: 'error', error: error?.name === 'NotAllowedError' ? 'Partage annulé.' : error.message });
    }
  }, [keepScreenOn, patch, sendChunk]);

  // Un collègue enregistre déjà : on suit sa réunion (participant : synthèse et notification), sans enregistrer.
  const joinTwin = useCallback(async () => {
    const { conflict } = state;
    if (!conflict) return;
    try {
      const meeting = await apiService.post(`/meetings/${conflict.meeting_id}/join`, {
        calendar_event_id: conflict.request?.calendar?.event?.id || null });
      setState({ phase: 'joined', meeting, projectName: conflict.request?.projectName, conflict });
    } catch (error) {
      patch({ phase: 'error', error: error.message });
    }
  }, [patch, state]);

  const recordAnyway = useCallback(() => {
    const request = state.conflict?.request;
    if (request) start({ ...request, allowDuplicate: true });
  }, [start, state.conflict]);

  // Envoie les tranches restantes puis clôt la réunion ; rejouable après une coupure.
  const finish = useCallback(async () => {
    const queue = queueRef.current;
    const meeting = meetingRef.current;
    const { chunkCount, duration } = stoppedRef.current || {};
    if (!queue || !meeting || chunkCount === undefined) return;
    patch({ phase: 'sending', error: null });
    queue.error = null;
    queue.enqueue([]); // relance l'envoi arrêté par une erreur
    try {
      await queue.flush();
      let finalized;
      for (let attempt = 0; !finalized; attempt += 1) {
        const response = await fetch(`${apiService.getBaseUrl()}/meetings/${meeting.id}/finalize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...apiService.getHeaders() },
          body: JSON.stringify({ chunk_count: chunkCount, duration_seconds: Math.round(duration) }),
        });
        if (response.ok) {
          finalized = await response.json();
          break;
        }
        const error = await httpError(response);
        const missing = error.detail?.missing;
        if (attempt >= 3 || !Array.isArray(missing)) throw error;
        // Le serveur signale des tranches manquantes : on les renvoie puis on recommence.
        queue.enqueue(missing);
        await queue.flush();
      }
      queue.close();
      queueRef.current = null;
      stoppedRef.current = null;
      patch({ phase: 'done', meeting: finalized });
    } catch (error) {
      patch({ phase: 'error', canRetry: true,
        error: `L'envoi n'a pas abouti : ${error.message}. Garde cet onglet ouvert et réessaie.` });
    }
  }, [patch]);

  const stop = useCallback(async () => {
    const capture = captureRef.current;
    if (!capture || !queueRef.current) return;
    patch({ phase: 'sending' });
    releaseScreen();
    const duration = capture.elapsedSeconds;
    const chunkCount = await capture.stop();
    captureRef.current = null;
    stoppedRef.current = { chunkCount, duration };
    await finish();
  }, [finish, patch, releaseScreen]);

  const cancel = useCallback(async () => {
    const meeting = meetingRef.current;
    await captureRef.current?.stop().catch(() => {});
    captureRef.current = null;
    queueRef.current?.close();
    queueRef.current = null;
    stoppedRef.current = null;
    releaseScreen();
    if (meeting) await apiService.post(`/meetings/${meeting.id}/cancel`, {}).catch(() => {});
    meetingRef.current = null;
    setState({ phase: 'idle' });
  }, [releaseScreen]);

  const dismiss = useCallback(() => {
    if (captureRef.current || queueRef.current) return;
    meetingRef.current = null;
    setState({ phase: 'idle' });
  }, []);

  // Fenêtre « Démarrer une réunion », ouverte depuis la barre de navigation ou une page projet.
  const [dialog, setDialog] = useState(null);
  const openDialog = useCallback((preset = {}) => setDialog(preset), []);
  const closeDialog = useCallback(() => setDialog(null), []);

  const value = useMemo(() => ({
    ...state, level, elapsed, upload, start, stop, cancel, dismiss, retrySend: finish, dialog, openDialog, closeDialog,
    joinTwin, recordAnyway,
  }), [state, level, elapsed, upload, start, stop, cancel, dismiss, finish, dialog, openDialog, closeDialog, joinTwin,
    recordAnyway]);

  return <MeetingRecorderContext.Provider value={value}>{children}</MeetingRecorderContext.Provider>;
};

export const useMeetingRecorder = () => {
  const context = useContext(MeetingRecorderContext);
  if (!context) throw new Error('useMeetingRecorder doit être utilisé dans MeetingRecorderProvider');
  return context;
};
